<script>
import { getQualitySafetyGuaranteeCommitment, submitShopApply } from "@/api/shopApply"

const LICENSE_FIELDS = Object.freeze([
    {
        field: "business_license",
        label: "营业执照",
        description: "请上传清晰完整的营业执照原件照片"
    },
    {
        field: "quality_safety_guarantee_commitment",
        label: "质量安全保证承诺书",
        description: "请上传清晰完整的质量安全保证承诺书"
    },
    {
        field: "drug_business_license",
        label: "药品经营许可证",
        description: "请上传在有效期内的药品经营许可证"
    },
    {
        field: "two_device_certificate",
        label: "二类医疗器械备案凭证",
        description: "请上传清晰完整的二类医疗器械备案凭证"
    },
    {
        field: "medical_device_business_license",
        label: "医疗器械经营许可证",
        description: "请上传在有效期内的医疗器械经营许可证"
    },
    {
        field: "food_business_license",
        label: "食品经营许可证",
        description: "请上传在有效期内的食品经营许可证"
    }
])

const UPLOAD_SUCCESS_CODES = [0, 10068]

const createInitialForm = () => ({
    name: "",
    company_name: "",
    credit_code: "",
    company_address: "",
    contact_name: "",
    phone: "",
    complaint_phone: "",
    complaint_email: "",
    business_license: "",
    quality_safety_guarantee_commitment: "",
    drug_business_license: "",
    two_device_certificate: "",
    medical_device_business_license: "",
    food_business_license: ""
})

const createUploadState = () => ({
    business_license: false,
    quality_safety_guarantee_commitment: false,
    drug_business_license: false,
    two_device_certificate: false,
    medical_device_business_license: false,
    food_business_license: false
})

const createUploadFileTypeState = () => ({
    business_license: "",
    quality_safety_guarantee_commitment: "",
    drug_business_license: "",
    two_device_certificate: "",
    medical_device_business_license: "",
    food_business_license: ""
})

export default {
    name: "MerchantApplyDialog",
    props: {
        visible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const validateMobile = (rule, value, callback) => {
            if (!/^1[3-9]\d{9}$/.test(value)) {
                callback(new Error("请输入正确的联系电话"))
                return
            }
            callback()
        }
        const validateComplaintPhone = (rule, value, callback) => {
            const phone = String(value || "").replace(/\s+/g, "")
            const isValid = /^(?:\+?86-?)?(?:1[3-9]\d{9}|0\d{2,3}-?\d{7,8}|(?:400|800)-?\d{3}-?\d{4})$/.test(phone)
            if (!isValid) {
                callback(new Error("请输入正确的投诉电话"))
                return
            }
            callback()
        }

        return {
            form: createInitialForm(),
            licenseFields: LICENSE_FIELDS,
            uploading: createUploadState(),
            pendingUploadFileTypes: createUploadFileTypeState(),
            uploadedFileTypes: createUploadFileTypeState(),
            commitmentTemplateUrl: "",
            commitmentTemplateLoading: false,
            submitting: false,
            rules: {
                name: [
                    { required: true, message: "请输入商户名称", trigger: "blur" },
                    { max: 100, message: "商户名称不能超过100个字符", trigger: "blur" }
                ],
                company_name: [
                    { required: true, message: "请输入公司名称", trigger: "blur" },
                    { max: 100, message: "公司名称不能超过100个字符", trigger: "blur" }
                ],
                credit_code: [
                    { required: true, message: "请输入统一社会信用代码", trigger: "blur" },
                    {
                        pattern: /^[0-9A-HJ-NPQRTUWXY]{18}$/,
                        message: "请输入正确的18位统一社会信用代码",
                        trigger: "blur"
                    }
                ],
                company_address: [
                    { required: true, message: "请输入公司地址", trigger: "blur" },
                    { max: 200, message: "公司地址不能超过200个字符", trigger: "blur" }
                ],
                contact_name: [
                    { required: true, message: "请输入联系人", trigger: "blur" },
                    { max: 30, message: "联系人不能超过30个字符", trigger: "blur" }
                ],
                phone: [
                    { required: true, message: "请输入联系电话", trigger: "blur" },
                    { validator: validateMobile, trigger: "blur" }
                ],
                complaint_phone: [
                    { required: true, message: "请输入投诉电话", trigger: "blur" },
                    { validator: validateComplaintPhone, trigger: "blur" }
                ],
                complaint_email: [
                    { required: true, message: "请输入投诉邮箱", trigger: "blur" },
                    { type: "email", message: "请输入正确的投诉邮箱", trigger: "blur" },
                    { max: 100, message: "投诉邮箱不能超过100个字符", trigger: "blur" }
                ],
                business_license: [
                    { required: true, message: "请上传营业执照", trigger: "change" }
                ],
                quality_safety_guarantee_commitment: [
                    { required: true, message: "请上传质量安全保证承诺书", trigger: "change" }
                ]
            }
        }
    },
    computed: {
        imageUploadAction() {
            return `${String(this.$config.baseUrl || "").replace(/\/$/, "")}/api/i/image`
        },
        pdfUploadAction() {
            return `${String(this.$config.baseUrl || "").replace(/\/$/, "")}/api/upload/pdf`
        },
        isUploading() {
            return Object.keys(this.uploading).some(field => this.uploading[field])
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler(value) {
                if (value) this.loadCommitmentTemplate()
            }
        }
    },
    methods: {
        resolveCommitmentTemplateUrl(data) {
            if (!data || typeof data.quality_safety_guarantee_commitment !== "string") return ""
            return data.quality_safety_guarantee_commitment.trim()
        },
        normalizeTemplateUrl(url) {
            if (!url) return ""
            try {
                return new URL(url, this.$config.baseUrl).href
            } catch (error) {
                return url
            }
        },
        getCommitmentTemplateFileName() {
            try {
                const filePath = new URL(this.commitmentTemplateUrl).pathname
                const sourceFileName = decodeURIComponent(filePath.split("/").pop() || "")
                const extension = sourceFileName.match(/\.[^.]+$/)
                return `质量安全保证承诺书模板${extension ? extension[0] : ""}`
            } catch (error) {
                return "质量安全保证承诺书模板"
            }
        },
        async loadCommitmentTemplate() {
            if (this.commitmentTemplateLoading) return

            this.commitmentTemplateLoading = true
            try {
                const response = await getQualitySafetyGuaranteeCommitment()
                if (Number(response && response.code) !== 0) {
                    this.commitmentTemplateUrl = ""
                    return
                }
                this.commitmentTemplateUrl = this.normalizeTemplateUrl(
                    this.resolveCommitmentTemplateUrl(response.data)
                )
            } catch (error) {
                this.commitmentTemplateUrl = ""
            } finally {
                this.commitmentTemplateLoading = false
            }
        },
        handleDownloadCommitmentTemplate() {
            if (!this.commitmentTemplateUrl) {
                this.$message.warning("承诺书模板暂不可用，请稍后重试")
                this.loadCommitmentTemplate()
                return
            }

            const link = document.createElement("a")
            link.href = this.commitmentTemplateUrl
            link.target = "_blank"
            link.rel = "noopener noreferrer"
            link.download = this.getCommitmentTemplateFileName()
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        handleCancel() {
            if (this.submitting) return
            this.$emit("update:visible", false)
        },
        handleClosed() {
            this.form = createInitialForm()
            this.uploading = createUploadState()
            this.pendingUploadFileTypes = createUploadFileTypeState()
            this.uploadedFileTypes = createUploadFileTypeState()
            this.$nextTick(() => {
                if (this.$refs.applyForm) this.$refs.applyForm.clearValidate()
            })
        },
        getUploadAction(field) {
            return this.pendingUploadFileTypes[field] === "pdf"
                ? this.pdfUploadAction
                : this.imageUploadAction
        },
        beforeUpload(field, file) {
            const fileName = String(file.name || "").toLowerCase()
            const isPdf = file.type === "application/pdf" || /\.pdf$/.test(fileName)
            const isImage = ["image/jpeg", "image/png"].includes(file.type) || /\.(jpe?g|png)$/.test(fileName)
            const isAllowedType = isImage || isPdf

            if (!isAllowedType) {
                this.$message.error("仅支持 JPG、JPEG、PNG、PDF 格式的文件")
                return false
            }

            this.pendingUploadFileTypes[field] = isPdf ? "pdf" : "image"
            this.uploading[field] = true

            // 等待 action 根据文件类型更新后，再由 el-upload 发起请求。
            return new Promise(resolve => {
                this.$nextTick(() => resolve(file))
            })
        },
        resolveUploadedFilePath(response) {
            const data = response && response.data
            if (typeof data === "string") return data
            if (!data || typeof data !== "object") return ""

            const pathFields = ["pic_path", "file_path", "pdf_path", "file_url", "url", "path"]
            const matchedField = pathFields.find(field => typeof data[field] === "string" && data[field])
            return matchedField ? data[matchedField] : ""
        },
        handleUploadSuccess(field, response, file) {
            this.uploading[field] = false

            const filePath = this.resolveUploadedFilePath(response)
            const responseCode = Number(response && response.code)
            const isUploadSuccess = UPLOAD_SUCCESS_CODES.includes(responseCode)
            if (!isUploadSuccess || !filePath) {
                this.$message.error((response && response.message) || "文件上传失败，请重试")
                return
            }

            // 图片与 PDF 上传接口返回的文件路径可直接用于回显和提交。
            this.form[field] = filePath
            const rawFile = (file && file.raw) || file || {}
            this.uploadedFileTypes[field] = this.pendingUploadFileTypes[field] === "pdf"
                || rawFile.type === "application/pdf"
                || /\.pdf$/i.test(rawFile.name || filePath)
                ? "pdf"
                : "image"
            this.$nextTick(() => {
                if (this.$refs.applyForm) this.$refs.applyForm.clearValidate(field)
            })
        },
        handleUploadError(field) {
            this.uploading[field] = false
            this.$message.error("文件上传失败，请检查网络后重试")
        },
        removeImage(field) {
            this.form[field] = ""
            this.uploadedFileTypes[field] = ""
            this.$nextTick(() => {
                if (this.$refs.applyForm) this.$refs.applyForm.validateField(field)
            })
        },
        handleCreditCodeInput(value) {
            this.form.credit_code = String(value || "")
                .toUpperCase()
                .replace(/[^0-9A-Z]/g, "")
                .slice(0, 18)
        },
        handleSubmit() {
            if (this.isUploading) {
                this.$message.warning("文件正在上传，请稍候")
                return
            }

            this.$refs.applyForm.validate(async valid => {
                if (!valid || this.submitting) return

                this.submitting = true
                try {
                    const response = await submitShopApply({
                        name: this.form.name,
                        company_name: this.form.company_name,
                        credit_code: this.form.credit_code,
                        company_address: this.form.company_address,
                        contact_name: this.form.contact_name,
                        phone: this.form.phone,
                        complaint_phone: this.form.complaint_phone,
                        complaint_email: this.form.complaint_email,
                        business_license: this.form.business_license,
                        quality_safety_guarantee_commitment: this.form.quality_safety_guarantee_commitment,
                        drug_business_license: this.form.drug_business_license,
                        two_device_certificate: this.form.two_device_certificate,
                        medical_device_business_license: this.form.medical_device_business_license,
                        food_business_license: this.form.food_business_license
                    })
                    if (response.code !== 0) {
                        this.$message.error(response.message || "入驻申请提交失败，请重试")
                        return
                    }

                    this.$emit("success", response.data)
                    this.$emit("update:visible", false)
                } catch (error) {
                    this.$message.error(error.message || "入驻申请提交失败，请重试")
                } finally {
                    this.submitting = false
                }
            })
        }
    }
}
</script>

<template>
    <el-dialog
        class="merchant-apply-dialog"
        title="商家入驻申请"
        :visible="visible"
        width="1120px"
        top="5vh"
        :close-on-click-modal="false"
        :close-on-press-escape="!submitting"
        :show-close="!submitting"
        append-to-body
        @close="handleCancel"
        @closed="handleClosed"
    >
        <div slot="title" class="dialog-heading">
            <span class="dialog-heading__icon" aria-hidden="true">
                <i class="el-icon-office-building"></i>
            </span>
            <div class="dialog-heading__content">
                <h2 class="dialog-heading__title">商家入驻申请</h2>
                <p class="dialog-heading__description">完善企业信息并提交资质，我们将在 1–3 个工作日内完成审核</p>
            </div>
        </div>

        <div class="review-notice" role="note">
            <div class="review-notice__item">
                <i class="el-icon-lock" aria-hidden="true"></i>
                <span>资料仅用于平台入驻审核</span>
            </div>
            <div class="review-notice__item">
                <i class="el-icon-circle-check" aria-hidden="true"></i>
                <span>请确保信息真实、证照清晰且在有效期内</span>
            </div>
        </div>

        <el-form
            ref="applyForm"
            class="apply-form"
            :model="form"
            :rules="rules"
            label-position="top"
        >
            <section class="form-section" aria-labelledby="company-info-title">
                <header class="section-heading">
                    <span class="section-heading__index">01</span>
                    <div>
                        <h3 id="company-info-title" class="section-heading__title">企业与联系人</h3>
                        <p class="section-heading__description">请填写营业执照登记信息及便于联系的负责人信息</p>
                    </div>
                </header>

                <div class="form-grid">
                    <el-form-item label="商户名称" prop="name">
                        <el-input
                            v-model.trim="form.name"
                            maxlength="100"
                            placeholder="请输入商户名称"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="公司名称" prop="company_name">
                        <el-input
                            v-model.trim="form.company_name"
                            maxlength="100"
                            placeholder="请输入营业执照上的公司全称"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="统一社会信用代码" prop="credit_code">
                        <el-input
                            v-model="form.credit_code"
                            maxlength="18"
                            placeholder="请输入18位统一社会信用代码"
                            clearable
                            @input="handleCreditCodeInput"
                        />
                    </el-form-item>
                    <el-form-item label="公司地址" prop="company_address">
                        <el-input
                            v-model.trim="form.company_address"
                            maxlength="200"
                            placeholder="请输入公司详细地址"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="联系人" prop="contact_name">
                        <el-input
                            v-model.trim="form.contact_name"
                            maxlength="30"
                            placeholder="请输入联系人姓名"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="联系电话" prop="phone">
                        <el-input
                            v-model.trim="form.phone"
                            maxlength="11"
                            placeholder="请输入11位手机号码"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="投诉电话" prop="complaint_phone">
                        <el-input
                            v-model.trim="form.complaint_phone"
                            maxlength="20"
                            placeholder="请输入投诉电话"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="投诉邮箱" prop="complaint_email">
                        <el-input
                            v-model.trim="form.complaint_email"
                            maxlength="100"
                            placeholder="请输入投诉邮箱"
                            clearable
                        />
                    </el-form-item>
                </div>
            </section>

            <section class="form-section form-section--license" aria-labelledby="license-info-title">
                <header class="section-heading">
                    <span class="section-heading__index">02</span>
                    <div>
                        <h3 id="license-info-title" class="section-heading__title">企业资质</h3>
                        <p class="section-heading__description">支持 JPG、JPEG、PNG、PDF 格式，文件不要过大</p>
                    </div>
                </header>

                <div class="license-grid">
                    <el-form-item
                        v-for="license in licenseFields"
                        :key="license.field"
                        class="license-form-item"
                        :label="license.label"
                        :prop="license.field"
                    >
                        <template slot="label">
                            <span>{{ license.label }}</span>
                            <el-button
                                v-if="license.field === 'quality_safety_guarantee_commitment'"
                                class="template-download-button"
                                type="text"
                                icon="el-icon-download"
                                :loading="commitmentTemplateLoading"
                                @click.prevent.stop="handleDownloadCommitmentTemplate"
                            >下载模板</el-button>
                        </template>
                        <div
                            v-loading="uploading[license.field]"
                            class="upload-frame"
                            :class="{ 'is-uploaded': form[license.field] }"
                        >
                            <el-upload
                                class="license-uploader"
                                :action="getUploadAction(license.field)"
                                :show-file-list="false"
                                accept=".jpg,.jpeg,.png,.pdf,application/pdf"
                                :aria-label="`上传${license.label}`"
                                :before-upload="file => beforeUpload(license.field, file)"
                                :on-success="(response, file) => handleUploadSuccess(license.field, response, file)"
                                :on-error="() => handleUploadError(license.field)"
                            >
                                <div v-if="form[license.field]" class="license-preview">
                                    <div v-if="uploadedFileTypes[license.field] === 'pdf'" class="pdf-preview">
                                        <i class="el-icon-document" aria-hidden="true"></i>
                                        <span>PDF 文件</span>
                                    </div>
                                    <img v-else :src="form[license.field]" :alt="`${license.label}预览`" />
                                    <div class="preview-status">
                                        <i class="el-icon-circle-check" aria-hidden="true"></i>
                                        <span>已上传</span>
                                    </div>
                                    <div class="preview-replace">
                                        <i class="el-icon-refresh" aria-hidden="true"></i>
                                        <span>点击更换文件</span>
                                    </div>
                                </div>
                                <div v-else class="upload-placeholder">
                                    <span class="upload-placeholder__icon" aria-hidden="true">
                                        <i class="el-icon-upload2"></i>
                                    </span>
                                    <strong>点击上传{{ license.label }}</strong>
                                    <span>{{ license.description }}</span>
                                </div>
                            </el-upload>
                            <el-button
                                v-if="form[license.field]"
                                class="remove-image-button"
                                type="text"
                                icon="el-icon-delete"
                                :aria-label="`删除${license.label}`"
                                @click.stop="removeImage(license.field)"
                            >删除</el-button>
                        </div>
                    </el-form-item>
                </div>
            </section>
        </el-form>

        <div slot="footer" class="dialog-footer">
            <p class="dialog-footer__hint">
                <i class="el-icon-info" aria-hidden="true"></i>
                提交即表示您确认所填资料真实有效
            </p>
            <div class="dialog-footer__actions">
                <el-button :disabled="submitting" @click="handleCancel">暂不申请</el-button>
                <el-button
                    type="primary"
                    :loading="submitting"
                    :disabled="isUploading"
                    @click="handleSubmit"
                >提交入驻申请</el-button>
            </div>
        </div>
    </el-dialog>
</template>

<style lang="scss">
.merchant-apply-dialog {
    .el-dialog {
        max-width: calc(100% - 32px);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(31, 35, 48, 0.16);
    }

    .el-dialog__header {
        padding: 22px 28px;
        border-bottom: 1px solid #ebeef2;
        background: linear-gradient(135deg, #fff 0%, #fff8fa 100%);
    }

    .el-dialog__headerbtn {
        top: 24px;
        right: 24px;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        transition: background-color 0.2s ease;

        &:hover,
        &:focus {
            background: #f4f5f7;
        }
    }

    .el-dialog__body {
        max-height: calc(90vh - 174px);
        box-sizing: border-box;
        padding: 20px 28px 24px;
        overflow-y: auto;
        background: #f7f8fa;
    }

    .el-dialog__footer {
        padding: 16px 28px;
        border-top: 1px solid #ebeef2;
        background: #fff;
    }

    .dialog-heading {
        display: flex;
        align-items: center;
        padding-right: 48px;
    }

    .dialog-heading__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        margin-right: 14px;
        flex-shrink: 0;
        border-radius: 10px;
        background: $base-color;
        color: #fff;
        box-shadow: 0 8px 18px rgba(255, 84, 123, 0.22);
        font-size: 21px;
    }

    .dialog-heading__content {
        min-width: 0;
    }

    .dialog-heading__title {
        margin: 0;
        color: #242832;
        font-size: 20px;
        font-weight: 600;
        line-height: 1.35;
    }

    .dialog-heading__description {
        margin: 4px 0 0;
        color: #737985;
        font-size: 13px;
        line-height: 1.5;
    }

    .review-notice {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 16px;
        padding: 11px 16px;
        border: 1px solid #f7dbe2;
        border-radius: 8px;
        background: #fff8fa;
        color: #626975;
        font-size: 12px;
    }

    .review-notice__item {
        display: flex;
        align-items: center;
        min-width: 0;

        i {
            margin-right: 7px;
            color: $base-color;
            font-size: 15px;
        }
    }

    .form-section {
        padding: 20px 22px 4px;
        border: 1px solid #ebeef2;
        border-radius: 10px;
        background: #fff;

        & + .form-section {
            margin-top: 16px;
        }
    }

    .form-section--license {
        padding-bottom: 6px;
    }

    .section-heading {
        display: flex;
        align-items: flex-start;
        margin-bottom: 18px;
    }

    .section-heading__index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        margin-right: 10px;
        flex-shrink: 0;
        border-radius: 8px;
        background: #fff0f4;
        color: $base-color;
        font-size: 12px;
        font-weight: 600;
    }

    .section-heading__title {
        margin: 0;
        color: #303540;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.4;
    }

    .section-heading__description {
        margin: 3px 0 0;
        color: #8a909c;
        font-size: 12px;
        line-height: 1.5;
    }

    .form-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 18px;
    }

    .license-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 14px;
        align-items: start;
    }

    .el-form-item {
        min-width: 0;
        margin-bottom: 18px;
    }

    .el-form-item__label {
        padding-bottom: 7px;
        color: #3f4550;
        font-size: 13px;
        font-weight: 500;
        line-height: 20px;
    }

    .el-input__inner {
        height: 40px;
        border-color: #dfe3e9;
        border-radius: 6px;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &:hover {
            border-color: #c8cdd5;
        }

        &:focus {
            border-color: $base-color;
            box-shadow: 0 0 0 3px rgba(255, 84, 123, 0.1);
        }
    }

    .license-form-item {
        margin-bottom: 2px;

        .el-form-item__label {
            min-height: 47px;
            box-sizing: border-box;
            padding-bottom: 7px;
            font-size: 12px;
            line-height: 20px;
            white-space: normal;
        }
    }

    .template-download-button {
        margin-left: 8px;
        padding: 0;
        color: $base-color;
        font-size: 12px;
        vertical-align: baseline;

        &:hover,
        &:focus {
            color: $base-color;
            opacity: 0.8;
        }
    }

    .upload-frame {
        position: relative;
        height: 150px;
        box-sizing: border-box;
        border: 1px dashed #cfd4dc;
        border-radius: 8px;
        overflow: hidden;
        background: #fafbfc;
        transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

        &:hover,
        &:focus-within {
            border-color: $base-color;
            background: #fffafb;
            box-shadow: 0 0 0 3px rgba(255, 84, 123, 0.08);
        }

        &.is-uploaded {
            border-style: solid;
            border-color: #dfe3e9;
            background: #fff;
        }
    }

    .license-uploader,
    .license-uploader .el-upload,
    .upload-placeholder,
    .license-preview {
        width: 100%;
        height: 100%;
    }

    .license-uploader .el-upload {
        display: block;
        text-align: left;
        cursor: pointer;
    }

    .upload-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        padding: 14px 10px;
        text-align: center;

        strong {
            margin-top: 8px;
            color: #3e4450;
            font-size: 12px;
            font-weight: 500;
            line-height: 1.35;
        }

        > span:last-child {
            max-width: 190px;
            margin-top: 5px;
            color: #969ca7;
            font-size: 11px;
            line-height: 1.4;
        }
    }

    .upload-placeholder__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #fff0f4;
        color: $base-color;
        font-size: 18px;
    }

    .license-preview {
        position: relative;
        box-sizing: border-box;
        padding: 8px;

        img {
            display: block;
            width: 100%;
            height: 100%;
            border-radius: 5px;
            object-fit: contain;
            background: #f4f5f7;
        }
    }

    .pdf-preview {
        display: flex;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        border-radius: 5px;
        background: #f4f5f7;
        color: #69707c;

        i {
            margin-bottom: 8px;
            color: $base-color;
            font-size: 36px;
        }

        span {
            font-size: 12px;
            font-weight: 500;
        }
    }

    .preview-status {
        position: absolute;
        top: 12px;
        left: 12px;
        display: inline-flex;
        align-items: center;
        padding: 4px 7px;
        border-radius: 14px;
        background: rgba(34, 148, 83, 0.92);
        color: #fff;
        font-size: 11px;
        line-height: 1;

        i {
            margin-right: 4px;
        }
    }

    .preview-replace {
        position: absolute;
        right: 8px;
        bottom: 8px;
        left: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 32px;
        border-radius: 0 0 5px 5px;
        background: rgba(22, 25, 31, 0.68);
        color: #fff;
        font-size: 11px;
        opacity: 0;
        transition: opacity 0.2s ease;

        i {
            margin-right: 6px;
        }
    }

    .is-uploaded:hover .preview-replace,
    .is-uploaded:focus-within .preview-replace {
        opacity: 1;
    }

    .remove-image-button {
        position: absolute;
        top: 8px;
        right: 10px;
        z-index: 2;
        padding: 4px 6px;
        border-radius: 4px;
        background: rgba(255, 255, 255, 0.95);
        color: #d14343;
        box-shadow: 0 2px 8px rgba(31, 35, 48, 0.12);

        &:hover,
        &:focus {
            background: #fff;
            color: #b92d2d;
        }
    }

    .dialog-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
    }

    .dialog-footer__hint {
        display: flex;
        align-items: center;
        margin: 0;
        color: #8a909c;
        font-size: 12px;
        line-height: 1.5;
        text-align: left;

        i {
            margin-right: 6px;
            color: #a7acb5;
        }
    }

    .dialog-footer__actions {
        display: flex;
        align-items: center;
        flex-shrink: 0;

        .el-button {
            min-width: 104px;
            height: 40px;
            border-radius: 6px;
        }

        .el-button--primary {
            min-width: 128px;
        }
    }
}

@media (max-width: 899px) {
    .merchant-apply-dialog {
        .license-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }
}

@media (max-width: 767px) {
    .merchant-apply-dialog {
        .el-dialog__header,
        .el-dialog__body,
        .el-dialog__footer {
            padding-right: 18px;
            padding-left: 18px;
        }

        .dialog-heading__description,
        .dialog-footer__hint {
            display: none;
        }

        .review-notice,
        .dialog-footer {
            align-items: stretch;
            flex-direction: column;
        }

        .form-grid,
        .license-grid {
            grid-template-columns: 1fr;
        }

        .dialog-footer__actions {
            justify-content: flex-end;
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .merchant-apply-dialog * {
        transition-duration: 0.01ms !important;
    }
}
</style>
